
namespace Serenity {

    export interface AutoColumnWidthMixinOptions {
        grid: Serenity.DataGrid<any, any>;
        /** Disable auto size on load behavior, default is false, user can stil auto size by double click */
        disabled?: boolean;
        /** True to just auto size once on load, not on every load, like after changing pages, sorting etc, default is false */
        autoSizeAllJustOnce?: boolean;
        /** Disable auto size on load behavior if user manually resizes a column, default is true */
        disableIfUserResize?: boolean;
        /** If column has an explicit width set in Form.cs, use that width on auto size, 
         * doesn't effect manually triggered auto size by double click, default is false */
        useExplicitWidths?: boolean;
        /** Set to true if headers shouldn't affect auto width, default is false.
         * If passed a number, header are considered for auto width but only up to that 
         * number, e.g. if you pass 200, and header width is 300, 200 will be used */
        ignoreHeaders?: boolean | number;
        /** Maximum rows to scan in data source, default is infinite */
        maxRows?: number;
        /** Maximum auto column width, default value is 400 */
        maxWidth?: number;
        /** Minimum auto column width, default value is 30 */
        minWidth?: number;
    }

    /**
     * A mixin that can be applied to a DataGrid for auto size column width functionality
     */
    export class AutoColumnWidthMixin extends Serenity.Widget<AutoColumnWidthMixinOptions> {

        private slickGrid: Slick.Grid;
        private $container: JQuery;
        private _disabled: boolean;
        private autoSizing: number = 0;
        private markupReadyOnce: boolean;
        private oldMarkupReady: () => void;
        private oldPersistSettings: (flags?: Serenity.GridPersistanceFlags) => void;
        private oldGetCurrentSettings: (flags?: Serenity.GridPersistanceFlags) => Serenity.PersistedGridSettings;
        private oldRestoreSettings: (settings?: Serenity.PersistedGridSettings, flags?: Serenity.GridPersistanceFlags) => void;
        public disabled: boolean;

        private columnResizeHandler = () => {
            if (!this.autoSizing && Q.coalesce(this.options.disableIfUserResize, true))
                this.disabled = true;
        }

        constructor(options: AutoColumnWidthMixinOptions) {
            super(options.grid.element, options);

            if (options.disabled != null)
                this.disabled = !!options.disabled;

            this.slickGrid = options.grid.slickGrid;
            this.$container = $((this.slickGrid as any).getContainerNode());
            this.$container.on('dblclick.' + this.uniqueName, '.slick-resizable-handle', (e) => this.resizeHandleClick(e));
            this.slickGrid.onColumnsResized.subscribe(this.columnResizeHandler);
            var self = this;

            this.oldMarkupReady = options.grid['markupReady'];
            options.grid['markupReady'] = function () {
                self.oldMarkupReady && self.oldMarkupReady.call(this);
                if (!self.disabled && (!self.markupReadyOnce || !self.options.autoSizeAllJustOnce))
                    self.autoSizeAll(self.disabled != null ? false : null);
                self.markupReadyOnce = true;
            };

            this.oldPersistSettings = options.grid['persistSettings'];
            options.grid['persistSettings'] = function (flags?: Serenity.GridPersistanceFlags) {
                if (self.autoSizing)
                    return;
                self.oldPersistSettings.call(this, flags);
            };

            this.oldGetCurrentSettings = options.grid['getCurrentSettings'];
            options.grid['getCurrentSettings'] = function (flags?: Serenity.GridPersistanceFlags): Serenity.PersistedGridSettings {
                flags = flags || this.gridPersistanceFlags();
                flags.columnWidths = Q.coalesce(flags.columnWidths, !!this.disabled);
                return self.oldGetCurrentSettings.call(this, flags);
            }

            this.oldRestoreSettings = options.grid['restoreSettings'];
            options.grid['restoreSettings'] = function (settings?: Serenity.PersistedGridSettings, flags?: Serenity.GridPersistanceFlags) {
                if (settings == null)
                    settings = this.getPersistedSettings();

                flags = flags || this.gridPersistanceFlags();

                if (settings != null &&
                    settings.columns &&
                    (flags.columnWidths || Q.any(settings.columns, x => x.width != null)))
                    self.disabled = true;

                self.oldRestoreSettings.call(this, settings, flags);
            }

            if (!this.disabled)
                this.autoSizeAll();
        }

        destroy() {
            if (this.options && this.options.grid) {
                this.options.grid.element.off('.' + this.uniqueName);
                this.options.grid['markupReady'] = this.oldMarkupReady;
            }

            this.slickGrid && this.slickGrid.onColumnsResized.unsubscribe(this.columnResizeHandler);

            this.options = null;
            this.slickGrid = null;
            this.$container = null;
            super.destroy();
        }


        private resizeHandleClick(e: JQueryEventObject) {
            if (e.ctrlKey) {
                e.preventDefault();
                e.stopPropagation();
                this.autoSizeAll(false);
                this.disabled = false;
                return;
            }

            var $header = $(e.currentTarget).closest('.slick-header-column');
            var columnDef = $header.data('column') as Slick.Column;

            if (!columnDef || !columnDef.resizable)
                return;

            e.preventDefault();
            e.stopPropagation();

            this.autoSizeColumn($header, columnDef);
        }

        private getAutoColumnWidth($header: JQuery, columnDef: Slick.Column) {
            var colIndex = this.slickGrid.getColumnIndex(columnDef.id);

            var autoWidth = this.getMaxColumnWidth(columnDef, colIndex);

            if (typeof this.options.ignoreHeaders == "number") {
                autoWidth = Math.max(Math.min(this.options.ignoreHeaders, getElementWidth($header[0])), autoWidth);
            }
            else if (!this.options.ignoreHeaders) {
                autoWidth = Math.max(getElementWidth($header[0]), autoWidth);
            }

            autoWidth = (autoWidth || 0) + 1;

            if (columnDef.maxWidth > 0)
                autoWidth = Math.min(autoWidth, columnDef.maxWidth);
            else
                autoWidth = Math.min(autoWidth, this.options.maxWidth || 400);

            if (columnDef.minWidth > 0)
                autoWidth = Math.max(autoWidth, columnDef.minWidth);
            else
                autoWidth = Math.max(autoWidth, this.options.minWidth || 30);

            return autoWidth;
        }

        private autoSizeColumn($header: JQuery, columnDef: Slick.Column) {
            var autoWidth = this.getAutoColumnWidth($header, columnDef);

            if (autoWidth !== columnDef.width) {
                this.autoSizing++;
                try {
                    columnDef.width = autoWidth;
                    this.slickGrid.setColumns(this.slickGrid.getColumns());
                    this.slickGrid.onColumnsResized.notify();
                }
                finally {
                    this.autoSizing--;
                }
            }
        }

        private queueAutoSize: number = 0;

        public autoSizeAll(useExplicitWidths?: boolean) {
            if (!this.$container.is(':visible')) {

                if (this.queueAutoSize <= 0) {
                    this.queueAutoSize++;
                    LazyLoadHelper.executeOnceWhenShown(this.$container, () => this.autoSizeAll(useExplicitWidths));
                }

                return;
            }

            if (this.queueAutoSize > 0) {
                this.queueAutoSize--;
            }

            this.autoSizing++;
            try {

                var $headers = this.$container.find(".slick-header-column");
                useExplicitWidths = Q.coalesce(useExplicitWidths, this.options.useExplicitWidths);
                $headers.each((index, el) => {
                    var columnDef = $(el).data('column') as Slick.Column;
                    if (!columnDef)
                        return;
                    var autoWidth = (useExplicitWidths && columnDef.sourceItem && columnDef.sourceItem.width > 0 && columnDef.sourceItem.widthSet) ?
                        columnDef.sourceItem.width : this.getAutoColumnWidth($(el), columnDef);
                    columnDef.width = autoWidth;
                });

                this.slickGrid.setColumns(this.slickGrid.getColumns());
                this.slickGrid.onColumnsResized.notify();
            }
            finally {
                this.autoSizing--;
            }
        }

        private createRow(columnDef: Slick.Column) {
            var rowEl = $('<div class="slick-row"><div class="slick-cell" style="text-overflow: initial; white-space: nowrap;"></div></div>');
            var gridCanvas = this.slickGrid.getCanvasNode();
            $(gridCanvas).append(rowEl);
            return rowEl;
        }

        private getMaxColumnWidth(columnDef: Slick.Column, colIndex: number) {
            var rowEl = this.createRow(columnDef);
            var maxAllowedWidth = columnDef.maxWidth > 0 ? columnDef.maxWidth : this.options.maxWidth;
            try {
                var cell = rowEl.children()[0];

                var length = this.slickGrid.getDataLength();
                if (this.options.maxRows != null &&
                    this.options.maxRows < length)
                    length = this.options.maxRows;

                var maxWidth = 0;
                var offset = 0;
                for (var i = 0; i < length; i++) {
                    var item = this.slickGrid.getDataItem(i);

                    if (columnDef.formatter) {
                        try {
                            var html = columnDef.formatter(-1, colIndex, item[columnDef.field], columnDef, item);
                            cell.innerHTML = html;
                        }
                        catch {
                            cell.innerText = Q.coalesce(item[columnDef.field], '');
                        }

                        var child = cell.childNodes[0] as HTMLElement;
                        if (child && child.tagName == "INPUT" &&
                            (child as HTMLInputElement).type != "checkbox") {
                            var cs = getComputedStyle(child);
                            offset = (parseFloat(cs.borderLeftWidth) || 0) +
                                (parseFloat(cs.borderRightWidth) || 0) +
                                (parseFloat(cs.paddingLeft) || 0) +
                                (parseFloat(cs.paddingRight) || 0);
                            cell.innerText = (child as HTMLInputElement).value;
                        }
                    }
                    else
                        cell.innerText = Q.coalesce(item[columnDef.field], '');

                    var width = cell.offsetWidth + offset;
                    if (width > maxWidth)
                        maxWidth = width;

                    if (maxAllowedWidth > 0 && width >= maxAllowedWidth)
                        break;
                }

                return maxWidth || 0;
            }
            finally {
                rowEl.remove();
            }
        }
    }

    function getElementWidth(element: HTMLElement) {
        var width, clone = element.cloneNode(true) as HTMLElement;
        clone.style.cssText = 'position: absolute; visibility: hidden; right: auto; text-overflow: initial; white-space: nowrap;';
        element.parentNode.insertBefore(clone, element);
        width = clone.offsetWidth;
        clone.parentNode.removeChild(clone);
        return width;
    }


}